/**
 * InlineMessage component - wrapper around web-palette MessageInline
 * Provides backward compatibility with existing InlineMessage API while using design system component
 *
 * @module views/components/InlineMessage
 * @memberof -Common
 */
import './InlineMessage.scss';

import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { MessageInline as PalMessageInline } from 'web-palette/dist/components/MessageInline/MessageInline';

import { devLogger } from '@ulta/core/utils/devMode/devMode';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { moveScreenReaderFocus } from '@ulta/utils/accessibility/accessibility';

import * as utils from './InlineMessage';

/**
 * Maps legacy messageType values to web-palette MessageInline variant values
 */
const messageStyleMap = {
  error: 'error',
  failure: 'error',
  warning: 'warning',
  infocircle: 'info',
  success: 'success',
  info: 'info',
  default: 'info'
};
/**
 * Represents an InlineMessage component
 *
 * @method
 * @param {InlineMessageProps} props - React properties passed from composition
 * @returns InlineMessage
 */
export const InlineMessage = function( props ){
  const {
    message,
    messageType,
    id,
    role,
    ariaAtomic,
    iconDisplay = defaultProps.iconDisplay,
    iconSize,
    shouldFocusOnMount = defaultProps.shouldFocusOnMount
  } = props;

  if( !message ){
    return null;
  }

  if( iconSize !== undefined ){
    devLogger( '[InlineMessage] iconSize prop is deprecated - MessageInline component always renders icons at 16x16 (lg)' );
  }

  const messageRef = useRef( null );

  useEffect( () => {
    utils.updateMessageFocus( { shouldFocusOnMount, messageRef }, { moveScreenReaderFocus } );
  }, [] );

  const messageVariant = messageStyleMap[messageType] || messageStyleMap.default;

  const paletteVariant = iconDisplay ? messageVariant : 'unstyled';

  return (
    <div
      className='InlineMessage'
      ref={ messageRef }
      { ...spreadProps( {
        id,
        role,
        'aria-atomic': ariaAtomic,
        tabIndex: shouldFocusOnMount ? -1 : undefined
      } ) }
    >
      <PalMessageInline
        variant={ paletteVariant }
        message={ message }
      />
    </div>
  );
};

/**
 * Update message focus handler function
 * @method
 * @param {object} data - Data passed as an argument
 * @param {object} methods - Methods passed as an argument
 */
export const updateMessageFocus = ( data, methods ) => {
  const { shouldFocusOnMount, messageRef } = data;
  const { moveScreenReaderFocus } = methods;
  if( shouldFocusOnMount && messageRef.current ){
    // When the component mounts with this flag, move the screen reader's focus
    moveScreenReaderFocus( messageRef.current );
  }
};

/**
 * Property type definitions
 * @typedef InlineMessageProps
 * @type {object}
 * @property {string} messageType - The type of message can be any one of ( 'error' | 'warning' | 'success' | 'info' | 'infocircle' | 'failure' )
 * @property {string} message - Detail of message can be passed as string
 * @property {string} iconSize - [DEPRECATED] This prop is no longer used - MessageInline component always renders icons at 16x16 (lg)
 * @property {boolean} iconDisplay - display icon true | false
 * @property {string} id - id for the message type class
 * @property {role} role - adds role attribute
 * @property {boolean} ariaAtomic - sets value for aria-atomic
 * @property {boolean} shouldFocusOnMount - If true, the component will attempt to focus itself on mount for screen readers.
 */
export const propTypes =  {
  messageType: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  id: PropTypes.string,
  iconSize: PropTypes.string,
  iconDisplay: PropTypes.bool,
  role: PropTypes.string,
  ariaAtomic: PropTypes.bool,
  shouldFocusOnMount: PropTypes.bool
};

export const defaultProps = {
  iconDisplay: true,
  shouldFocusOnMount: false
};

InlineMessage.propTypes = propTypes;
InlineMessage.defaultProps = defaultProps;

export default InlineMessage;
